import { useEffect, useMemo, useState } from "react";
import { PlayCircle, FilmSlate, Sparkle, X } from "@phosphor-icons/react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SampleVideosGallery — replaces the old single-URL showcase. Renders a niche-
 * filterable grid of finished sample outputs (Sora 2 generations + uploaded URLs).
 *
 * Source-of-truth: GET /api/sample-videos (public, only published+ready). Each
 * video doc carries either an external embed (YouTube/Vimeo/mp4) or a backend-
 * relative path /api/sample-videos/file/<id>.mp4 — we resolve relative paths
 * against REACT_APP_BACKEND_URL so prospects can play them on the landing page
 * before they ever sign up.
 */
export default function SampleVideosGallery({ variant = "landing" }) {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeNiche, setActiveNiche] = useState("ALL");
    const [playing, setPlaying] = useState(null);

    useEffect(() => {
        api.get("/sample-videos")
            .then((r) => setVideos(Array.isArray(r.data) ? r.data : []))
            .catch(() => setVideos([]))
            .finally(() => setLoading(false));
    }, []);

    // Hide the assembly-line walkthrough from the niche grid — it gets its own block.
    const niches = useMemo(() => {
        const set = new Set(videos.filter((v) => !v.is_walkthrough).map((v) => v.niche));
        return ["ALL", ...Array.from(set)];
    }, [videos]);

    const filtered = useMemo(() => {
        const samples = videos.filter((v) => !v.is_walkthrough);
        if (activeNiche === "ALL") return samples;
        return samples.filter((v) => v.niche === activeNiche);
    }, [videos, activeNiche]);

    if (loading) return null;

    return (
        <section
            id="sample-videos"
            data-testid="sample-videos-gallery"
            className={`relative max-w-7xl mx-auto px-6 md:px-12 lg:px-20 ${
                variant === "landing" ? "py-20 md:py-28" : "py-10"
            }`}
        >
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-4">
                    <FilmSlate size={14} weight="fill" className="text-electric-blue" />
                    <span className="label-mono text-[10px] text-electric-blue">SAMPLE OUTPUTS · BUILT WITH THE LAB</span>
                </div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-4">
                    Real outputs from the<br />
                    <span className="text-electric-blue neon-text">Creator Lab assembly line.</span>
                </h2>
                <p className="text-base md:text-lg text-foreground/75 max-w-2xl mx-auto">
                    Below is a sample of the cinematic faceless content built using a Recipe + Script Engine
                    + Visual Authority stack. Different niches, same system.
                </p>
            </div>

            {/* Niche filter pills */}
            {videos.length > 0 && niches.length > 2 && (
                <div className="flex flex-wrap items-center justify-center gap-2 mb-8" data-testid="sample-videos-filters">
                    {niches.map((n) => (
                        <button
                            key={n}
                            onClick={() => setActiveNiche(n)}
                            data-testid={`sample-videos-filter-${n.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-3.5 py-1.5 label-mono text-[10px] border transition-all ${
                                activeNiche === n
                                    ? "border-electric-blue text-electric-blue bg-electric-blue/10"
                                    : "border-white/15 text-muted-foreground hover:border-electric-blue/50 hover:text-white"
                            }`}
                        >
                            {n}
                        </button>
                    ))}
                </div>
            )}

            {filtered.length === 0 ? (
                <PlaceholderGrid />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="sample-videos-grid">
                    {filtered.map((v) => (
                        <SampleVideoCard
                            key={v.id}
                            video={v}
                            onPlay={() => setPlaying(v)}
                        />
                    ))}
                </div>
            )}

            {playing && (
                <VideoLightbox video={playing} onClose={() => setPlaying(null)} />
            )}
        </section>
    );
}

function resolveVideoUrl(url) {
    if (!url) return "";
    if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
    return url;
}

function isImageUrl(url) {
    return /\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(url);
}

function isDirectVideoUrl(url) {
    return /\.(mp4|webm|mov)(\?|$)/i.test(url) || url.includes("/api/sample-videos/file/") && !isImageUrl(url);
}

function SampleVideoCard({ video, onPlay }) {
    const url = resolveVideoUrl(video.video_url);
    const image = isImageUrl(url);
    const direct = !image && isDirectVideoUrl(url);
    return (
        <div
            data-testid={`sample-video-card-${video.id}`}
            className="group bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/60 transition-all flex flex-col"
        >
            <div className="relative aspect-[9/16] sm:aspect-video bg-black overflow-hidden">
                {image ? (
                    <img
                        src={url}
                        alt={video.title}
                        loading="lazy"
                        className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
                    />
                ) : direct ? (
                    <video
                        src={url}
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
                        onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                    />
                ) : (
                    <div className="absolute inset-0 grid-bg opacity-30" />
                )}
                {!image && (
                    <button
                        onClick={onPlay}
                        data-testid={`sample-video-play-${video.id}`}
                        className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-all"
                        aria-label={`Play ${video.title}`}
                    >
                        <PlayCircle size={64} weight="fill" className="text-electric-blue drop-shadow-[0_0_20px_rgba(0,123,255,0.7)] opacity-90 group-hover:scale-110 transition-transform" />
                    </button>
                )}
                {/* Gradient overlay on images for legibility */}
                {image && (
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />
                )}
                {video.source === "sora2" && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-black/70 border border-electric-blue/40 backdrop-blur">
                        <Sparkle size={10} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[9px] text-electric-blue">SORA 2</span>
                    </div>
                )}
                {video.source === "nano_banana" && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-black/70 border border-electric-blue/40 backdrop-blur">
                        <Sparkle size={10} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[9px] text-electric-blue">CINEMATIC STILL</span>
                    </div>
                )}
            </div>
            <div className="p-4">
                <p className="label-mono text-[10px] text-electric-blue mb-1.5">{video.niche}</p>
                <h3 className="font-heading font-bold text-base leading-tight mb-1.5">{video.title}</h3>
                {video.description && (
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{video.description}</p>
                )}
            </div>
        </div>
    );
}

function VideoLightbox({ video, onClose }) {
    const url = resolveVideoUrl(video.video_url);
    const direct = isDirectVideoUrl(url);
    return (
        <div
            data-testid="sample-video-lightbox"
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={onClose}
        >
            <button
                data-testid="sample-video-lightbox-close"
                className="absolute top-5 right-5 p-2 border border-white/20 hover:border-electric-blue text-white"
                onClick={onClose}
                aria-label="Close"
            >
                <X size={20} weight="bold" />
            </button>
            <div
                className="relative w-full max-w-4xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="relative w-full aspect-video bg-black border border-electric-blue/40 neon-border overflow-hidden">
                    {direct ? (
                        <video
                            src={url}
                            controls
                            autoPlay
                            playsInline
                            className="w-full h-full object-contain bg-black"
                        />
                    ) : (
                        <iframe
                            src={url}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-full"
                        />
                    )}
                </div>
                <div className="mt-4 px-1">
                    <p className="label-mono text-[10px] text-electric-blue mb-1">{video.niche}</p>
                    <h3 className="font-heading font-bold text-xl">{video.title}</h3>
                    {video.description && <p className="text-sm text-muted-foreground mt-1.5">{video.description}</p>}
                </div>
            </div>
        </div>
    );
}

function PlaceholderGrid() {
    return (
        <div
            data-testid="sample-videos-empty"
            className="relative w-full bg-gradient-to-br from-[#0A0A0A] to-[#0A1428] border border-electric-blue/30 neon-border p-10 md:p-16 text-center"
        >
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-80 bg-electric-blue/15 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
                <PlayCircle size={56} weight="fill" className="text-electric-blue mx-auto mb-4 opacity-60" />
                <p className="label-mono text-electric-blue mb-2">SAMPLE LIBRARY GOING LIVE</p>
                <p className="text-sm md:text-base text-foreground/70 max-w-md mx-auto leading-relaxed">
                    Fresh Sora 2 generations and Operator-built clips are dropping in. Check back
                    shortly — or jump to the case studies below for real Operator results.
                </p>
            </div>
        </div>
    );
}
